import { PermitType } from "common/types/permits";

/**
 * Permit application step enum for tracking progress through the multi-step form
 */
export enum PermitApplicationStep {
  SelectAuthority = 1,
  SelectPermitType = 2,
  ProvideInformation = 3,
  UploadDocuments = 4,
  ReviewConfirm = 5,
}

/**
 * Authority selection state
 */
export interface AuthorityState {
  readonly authorityId: string | null;
  readonly authorityName: string | null;
}

/**
 * Permit type selection state
 */
export interface PermitTypeSelectionState {
  readonly permitTypeId: string | null;
  /** Full permit type object for display purposes */
  readonly permitType: PermitType | null;
}

/**
 * Vehicle selection state
 */
export interface VehicleSelectionState {
  readonly selectedVehicleIds: readonly string[];
  readonly applyToAllVehicles: boolean;
  readonly autoAssignNewVehicles: boolean;
}

/**
 * Document state for a single required document type
 * Documents array is initialized from permitType.requiredDocumentTypes when permit type is selected
 */
export interface DocumentState {
  /** Required document type ID from permit type */
  readonly documentTypeId: string;
  /** Document type name for display */
  readonly documentTypeName: string;
  /** Document type description for tooltip */
  readonly documentTypeDescription: string | null;
  /** Document ID returned from presigned URL request (null if not uploaded) */
  readonly documentId: string | null;
}

/**
 * Complete permit application state
 */
export interface PermitApplicationState {
  readonly authority: AuthorityState;
  readonly permitTypeSelection: PermitTypeSelectionState;
  readonly vehicleSelection: VehicleSelectionState;
  readonly documents: readonly DocumentState[];
  /** ISO timestamp when terms were accepted (null if not accepted) */
  readonly acceptedTermsAt: string | null;
}

/**
 * Context value interface providing state and actions
 */
export interface IPermitApplicationContext {
  /** Current application state */
  readonly state: PermitApplicationState;

  /**
   * Set authority. If different from current, resets ALL downstream state
   * (permitType, vehicles, documents, terms)
   */
  readonly setAuthority: (authorityId: string | null, authorityName: string | null) => void;

  /**
   * Set permit type. If different from current, resets downstream state
   * (vehicles, documents, terms). Initializes documents from permitType.requiredDocumentTypes
   */
  readonly setPermitType: (permitTypeId: string, permitType: PermitType) => void;

  /**
   * Set vehicle selection
   */
  readonly setSelectedVehicles: (
    vehicleIds: string[],
    applyToAllVehicles: boolean,
    autoAssignNewVehicles: boolean,
  ) => void;

  /**
   * Update document ID for a specific document type (from presigned URL response)
   */
  readonly updateDocument: (documentTypeId: string, documentId: string) => void;

  /**
   * Clear document ID for a specific document type (when removing an uploaded document)
   */
  readonly clearDocument: (documentTypeId: string) => void;

  /**
   * Accept terms, setting acceptedTermsAt to current ISO timestamp
   */
  readonly acceptTerms: () => void;

  /**
   * Clear terms acceptance, setting acceptedTermsAt to null
   */
  readonly clearTerms: () => void;

  /**
   * Clear all state to initial values
   */
  readonly resetApplication: () => void;

  /**
   * Check if a specific step is complete
   */
  readonly isStepComplete: (step: PermitApplicationStep) => boolean;

  /**
   * Check if user can proceed to a specific step (all previous steps must be complete)
   */
  readonly canProceedToStep: (step: PermitApplicationStep) => boolean;
}
